import { privateSeats } from "./class-types.ts";
/**
 * How full one session is, in numbers and words (Studio Home, H1). Desk's Today card and the
 * phone's owner Home read it, so both say the same thing about the same session.
 *
 * A private session has one place. A group session with no limit (`capacity` null) is never full
 * and has no bar: the words say how many are booked.
 */
export type SessionFillInput = {
  kind: "group" | "private";
  capacity: number | null;
  bookedCount: number;
  waitlistCount?: number | undefined;
  firstTimers?: number | undefined;
  /** Booked people with a health note (C2): ids only. */
  healthFlags?: ReadonlyArray<{ participantId: string }> | undefined;
};

export type SessionFill = {
  booked: number;
  /** The places in the session. Null when the session has no limit. */
  capacity: number | null;
  /** Booked of the places, from 0 to 1. Null when the session has no limit. */
  share: number | null;
  full: boolean;
  /** "4 of 6 booked", "4 booked, no limit". */
  text: string;
  /** "4/6", or "4" with no limit: for a small chip. */
  short: string;
  waiting: number;
  /** "2 waiting", or null when nobody waits. */
  waitingText: string | null;
  firstTimers: number;
  /** "1 first visit", or null. */
  firstTimersText: string | null;
  healthNotes: number;
  /** "1 health note", or null: the team takes care, the answers are on the client page. */
  healthNotesText: string | null;
};

export function sessionFill(session: SessionFillInput): SessionFill {
  const booked = session.bookedCount;
  // A private session has one place, or two for a duet (S2).
  const capacity = session.kind === "private" ? privateSeats(session.capacity) : session.capacity;
  const waiting = session.waitlistCount ?? 0;
  const firstTimers = session.firstTimers ?? 0;
  const healthNotes = session.healthFlags?.length ?? 0;
  const share = capacity === null ? null : capacity <= 0 ? 1 : Math.min(1, booked / capacity);
  return {
    booked,
    capacity,
    share,
    full: capacity !== null && booked >= capacity,
    text: capacity === null ? `${booked} booked, no limit` : `${booked} of ${capacity} booked`,
    short: capacity === null ? String(booked) : `${booked}/${capacity}`,
    waiting,
    waitingText: waiting > 0 ? `${waiting} waiting` : null,
    firstTimers,
    firstTimersText: firstTimers > 0 ? `${firstTimers} first ${firstTimers === 1 ? "visit" : "visits"}` : null,
    healthNotes,
    healthNotesText: healthNotes > 0 ? `${healthNotes} health ${healthNotes === 1 ? "note" : "notes"}` : null,
  };
}

/**
 * The fill bar under a session (D38): booked of the places, from 0 to 1 (full is 1). It has one
 * colour, and turns gold when people wait (`waitlist`), so a screen never colours a session by how
 * full it is. Each app maps the tone to its own design token. Null for a session with no limit: it
 * has no bar. `text` is what a screen reader says for the bar: "6 of 10 places taken", "Full, 3 on
 * the waiting list".
 */
export type FillBar = { share: number; tone: "booked" | "waitlist"; text: string };

export function fillBar(fill: Pick<SessionFill, "booked" | "capacity" | "share" | "full" | "waiting">): FillBar | null {
  if (fill.capacity === null || fill.share === null) return null;
  const waiting = fill.waiting > 0 ? `${fill.waiting} on the waiting list` : null;
  const taken = fill.full ? "Full" : `${fill.booked} of ${fill.capacity} ${fill.capacity === 1 ? "place" : "places"} taken`;
  return { share: fill.share, tone: waiting ? "waitlist" : "booked", text: waiting ? `${taken}, ${waiting}` : taken };
}

/**
 * Fewer than half of the places are booked (Studio A1, "classes tomorrow under half full"). Never
 * for a session with no limit. A private session has its one or two places (`privateSeats`), so a
 * duet with nobody booked is under half and a private session with one place never is.
 */
export function underHalfFull(fill: Pick<SessionFill, "booked" | "capacity">): boolean {
  return fill.capacity !== null && fill.capacity > 1 && fill.booked * 2 < fill.capacity;
}
